import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PhoneCall, RefreshCw, AlertTriangle, ShieldAlert, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { LoadingBlock, ErrorState, toast } from "@/components/ds";

const STATUS_TONE: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  config_required: "bg-slate-100 text-slate-500",
  registration_required: "bg-slate-100 text-slate-500",
  created: "bg-blue-100 text-blue-700",
  pending: "bg-amber-100 text-amber-700",
};
function Pill({ text, kind }: { text: string; kind?: string }) {
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_TONE[kind || ""] || "bg-slate-100 text-slate-600"}`}>{text}</span>;
}
function Stat({ label, value, tone }: any) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`tel-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-0.5 text-xl font-semibold ${tone || "text-slate-900"}`}>{value}</p>
    </div>
  );
}

function ProfileDrawer({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({ queryKey: ["tel-profile", orgId], queryFn: () => api.telephonyAdminProfile(orgId) });
  const act = (fn: () => Promise<any>, ok: string) => async () => {
    try { await fn(); toast.success(ok); await refetch(); qc.invalidateQueries({ queryKey: ["tel-overview"] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} data-testid="tel-drawer">
      <div className="h-full w-full max-w-lg overflow-y-auto bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Telephony provisioning</h2>
          <button onClick={onClose} data-testid="tel-drawer-close"><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        {isLoading || !data ? <LoadingBlock rows={6} /> : (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-slate-900">{data.business_name || orgId}</p>
              <p className="text-xs text-slate-500">{data.industry || "—"} · {data.plan || "no plan"} · org {data.org_status}</p>
            </div>
            {!data.parent_configured && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-700" data-testid="tel-parent-warning">
                <ShieldAlert className="mt-0.5 h-4 w-4" /> ResFil parent Twilio account is not configured. Subaccount creation, number purchase and live calls are disabled until parent credentials are added (honest state — nothing is faked).
              </div>
            )}
            <dl className="space-y-1.5 rounded-lg border border-slate-200 p-4 text-sm">
              {[
                ["Provisioning status", data.provisioning_status],
                ["Subaccount", data.twilio_subaccount_sid ? "Created" : "Pending"],
                ["Subaccount SID", data.twilio_subaccount_sid || "—"],
                ["Number source", data.number_source || "not chosen"],
                ["Primary number", data.primary_phone_number || "—"],
                ["Voice webhook", data.voice_webhook_status],
                ["SMS webhook", data.sms_webhook_status],
                ["AI voice agent", data.voice_agent_id || "not linked"],
                ["Activation", data.telephony_activation_status],
                ["Compliance", data.compliance_status],
                ["Trust profile", data.trust_profile_status],
                ["A2P brand", data.a2p_brand_status],
                ["A2P campaign", data.a2p_campaign_status],
              ].map(([k, v]) => (
                <div key={k as string} className="flex justify-between gap-4">
                  <dt className="text-slate-500">{k}</dt><dd className="truncate font-medium text-slate-800">{v as string}</dd>
                </div>
              ))}
            </dl>
            {data.blocked_reason && <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600" data-testid="tel-blocked-reason">{data.blocked_reason}</p>}
            {data.provisioning_error && (
              <div className="rounded-lg bg-red-50 p-3 text-xs text-red-700" data-testid="tel-error">
                <p className="flex items-center gap-1 font-medium"><AlertTriangle className="h-3.5 w-3.5" /> {data.provisioning_error.code}</p>
                <p className="mt-0.5">{data.provisioning_error.message}</p>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button onClick={act(() => api.telephonyProvisionStart(orgId), "Provisioning started")} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700" data-testid="tel-btn-start">Start Provisioning</button>
              <button onClick={act(() => api.telephonyProvisionSubaccount(orgId), "Subaccount step run")} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="tel-btn-subaccount">Create Subaccount</button>
              <button onClick={act(() => api.telephonyNumberSource(orgId, "new"), "Number source: new")} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="tel-btn-new-number">Choose New Number</button>
              <button onClick={act(() => api.telephonyNumberSource(orgId, "existing"), "Number source: existing")} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="tel-btn-existing-number">Connect Existing Number</button>
              {data.provisioning_error && <button onClick={act(() => api.telephonyProvisionRetry(orgId), "Retried")} className="rounded-lg border border-amber-300 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50" data-testid="tel-btn-retry">Retry Failed Step</button>}
              <button onClick={() => refetch()} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="tel-btn-refresh"><RefreshCw className="h-4 w-4" /></button>
            </div>
            <p className="text-[11px] text-slate-400">Phase 2 will add live number search/purchase, voice routing to the AI agent and a real test call. Phase 3 adds A2P compliance & SMS activation.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Telephony() {
  const [selected, setSelected] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const params = statusFilter ? { provisioning_status: statusFilter } : undefined;
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["tel-overview", statusFilter], queryFn: () => api.telephonyAdminOverview(params) });
  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;
  const c = data.counts;
  return (
    <div data-testid="internal-telephony">
      <div className="mb-6 flex items-center gap-2">
        <PhoneCall className="h-6 w-6 text-blue-600" />
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Telephony Command Center</h1>
          <p className="text-sm text-slate-500">Per-customer Twilio provisioning across the platform.</p>
        </div>
      </div>

      {!data.parent_configured && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="tel-platform-warning">
          <ShieldAlert className="mt-0.5 h-4 w-4" />
          <span><b>Platform provider readiness:</b> ResFil parent Twilio account is <b>not connected</b>. Customer subaccounts, numbers and live calls are disabled until parent credentials are added — statuses below are honest and never faked.</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
        <Stat label="Subaccounts" value={c.subaccounts_created} tone="text-blue-600" />
        <Stat label="Voice Active" value={c.voice_active} tone="text-emerald-600" />
        <Stat label="SMS Active" value={c.sms_active} tone="text-emerald-600" />
        <Stat label="Voice Only" value={c.voice_only} tone="text-amber-600" />
        <Stat label="A2P Pending" value={c.a2p_pending} tone="text-amber-600" />
        <Stat label="A2P Rejected" value={c.a2p_rejected} tone="text-red-600" />
        <Stat label="Numbers Assigned" value={c.numbers_assigned} />
        <Stat label="Numbers Unassigned" value={c.numbers_unassigned} />
        <Stat label="Provisioning Failed" value={c.provisioning_failed} tone="text-red-600" />
        <Stat label="Provider Errors" value={c.provider_errors} tone="text-red-600" />
        <Stat label="Total Profiles" value={c.total_profiles} />
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-800">Customer telephony ({data.profiles.length})</h2>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" data-testid="tel-filter-status">
            <option value="">All statuses</option>
            {data.provisioning_states.map((s: string) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500">{["Business","Plan","Status","Subaccount","Number","Voice","SMS","A2P",""].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
            <tbody>
              {data.profiles.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-500">No telephony profiles yet. They are created automatically when a customer completes payment.</td></tr>}
              {data.profiles.map((r: any) => (
                <tr key={r.organization_id} className="cursor-pointer border-t border-slate-50 hover:bg-slate-50" onClick={() => setSelected(r.organization_id)} data-testid={`tel-row-${r.organization_id}`}>
                  <td className="px-3 py-2 font-medium text-slate-900">{r.business_name || r.organization_id}{r.has_error && <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-red-500" />}</td>
                  <td className="px-3 py-2 capitalize text-slate-500">{r.plan || "—"}</td>
                  <td className="px-3 py-2"><Pill text={r.provisioning_status} kind={r.provisioning_status === "FAILED" ? "pending" : undefined} /></td>
                  <td className="px-3 py-2"><Pill text={r.subaccount} kind={r.subaccount} /></td>
                  <td className="px-3 py-2 text-slate-600">{r.primary_phone_number || "—"}</td>
                  <td className="px-3 py-2"><Pill text={r.voice === "active" ? "Active" : "Config"} kind={r.voice} /></td>
                  <td className="px-3 py-2"><Pill text={r.sms === "active" ? "Active" : "Reg. req."} kind={r.sms} /></td>
                  <td className="px-3 py-2 text-slate-500">{r.a2p_campaign_status}</td>
                  <td className="px-3 py-2 text-blue-600">Manage →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <ProfileDrawer orgId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
